// src/features/dashboard/components/EmployeeActiveHoursReport/HourlyActiveMinutesChart.tsx

import { useMemo } from 'react';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Cell,
  Tooltip,
} from 'recharts';

import type {
  HourlyActiveMinutesItem,
} from '../../types/EmployeeActiveHours/employeeActiveHours.types';

/* ============================================================================
 * API RESPONSE
 * ========================================================================== */

interface HourlyActiveMinutesApiResponse {
  hourlyActiveMinutesTarget?: string | number;

  employeeHourlyActiveMinutesList?: HourlyActiveMinutesItem[];
}

/* ============================================================================
 * PROPS
 * ========================================================================== */

interface Props {
  data?:
    | HourlyActiveMinutesItem[]
    | HourlyActiveMinutesApiResponse
    | null;

  target?: number | string;
}

/* ============================================================================
 * CHART DATA
 * ========================================================================== */

interface ChartDataItem {
  time: string;
  hour: number;
  activeMinutes: number;
}

/* ============================================================================
 * COLORS
 * ========================================================================== */

const BAR_COLOR = '#7C4DDF';

const GRID_COLOR = '#EAECF0';

const AXIS_COLOR = '#475467';

const THRESHOLD_COLOR = '#6B7FA3';

// Keep the empty fallback stable so useMemo dependencies do not change
// on every render when the API list is missing.
const EMPTY_HOURLY_ACTIVE_MINUTES: HourlyActiveMinutesItem[] = [];

/* ============================================================================
 * CHART SETTINGS
 * ========================================================================== */

const CHART_HEIGHT = 230;

const CHART_TOP_MARGIN = 28;

const CHART_BOTTOM_MARGIN = 5;

/* ============================================================================
 * SMALL TOOLTIP
 *
 * Example:
 *
 * ┌──────────────────────┐
 * │ 06:00 - 07:00        │
 * │ ● Active mins: 56.43m│
 * └──────────────────────┘
 *
 * ========================================================================== */

interface CustomTooltipProps {
  active?: boolean;

  payload?: Array<{
    payload?: ChartDataItem;

    value?: number | string;
  }>;
}

const CustomTooltip = ({
  active,
  payload,
}: CustomTooltipProps) => {
  if (
    !active ||
    !payload ||
    payload.length === 0
  ) {
    return null;
  }

  const item =
    payload[0]?.payload;

  if (!item) {
    return null;
  }

  const value =
    Number(
      item.activeMinutes,
    );

  /*
   * Never show tooltip for zero-value hours.
   *
   * Example:
   *
   * 08:00 = 0m
   *
   * No tooltip.
   */

  if (
    !Number.isFinite(value) ||
    value <= 0
  ) {
    return null;
  }

  const formattedValue =
    value % 1 === 0
      ? value.toFixed(0)
      : value.toFixed(2);

  const startHour =
    String(
      item.hour,
    ).padStart(2, '0');

  const endHour =
    String(
      item.hour === 23
        ? 24
        : item.hour + 1,
    ).padStart(2, '0');

  return (
    <div
      className="
        w-[155px]
        overflow-hidden
        rounded-[8px]
        border
        border-[#D0D5DD]
        bg-white
        shadow-[0_3px_10px_rgba(16,24,40,0.16)]
      "
    >
      {/* ================================================================
          TIME RANGE

          06:00 - 07:00
      ================================================================= */}

      <div
        className="
          border-b
          border-[#EAECF0]
          px-3
          py-[7px]
        "
      >
        <span
          className="
            whitespace-nowrap
            text-[12px]
            font-medium
            leading-[16px]
            text-[#344054]
          "
        >
          {startHour}:00 - {endHour}:00
        </span>
      </div>

      {/* ================================================================
          ACTIVE VALUE
      ================================================================= */}

      <div
        className="
          flex
          items-center
          gap-[7px]
          px-3
          py-[8px]
        "
      >
        {/* Purple dot */}

        <span
          className="
            h-[10px]
            w-[10px]
            shrink-0
            rounded-full
            bg-[#7C4DDF]
          "
        />

        {/* Label */}

        <span
          className="
            whitespace-nowrap
            text-[12px]
            text-[#344054]
          "
        >
          Active mins:
        </span>

        {/* Value */}

        <span
          className="
            ml-auto
            whitespace-nowrap
            text-[12px]
            font-semibold
            text-[#101828]
          "
        >
          {formattedValue}m
        </span>
      </div>
    </div>
  );
};

/* ============================================================================
 * THRESHOLD LABEL
 *
 * RIGHT SIDE OF THE DASHED LINE
 *
 * Example:
 *
 * ------------------------------------ 45m threshold
 *
 * ========================================================================== */

const ThresholdLabel = ({
  threshold,
  yAxisMax,
}: {
  threshold: number;
  yAxisMax: number;
}) => {
  const plotHeight =
    CHART_HEIGHT -
    CHART_TOP_MARGIN -
    CHART_BOTTOM_MARGIN;

  const thresholdY =
    CHART_TOP_MARGIN +
    (1 - threshold / yAxisMax) *
      plotHeight;

  return (
    <div
      className="
        pointer-events-none
        absolute
        right-[2px]
        z-[30]
        whitespace-nowrap
        rounded-[3px]
        border
        border-[#98A2B3]
        bg-white
        px-[5px]
        py-[1px]
        text-[10px]
        font-medium
        leading-[14px]
        text-[#1D4ED8]
      "
      style={{
        /*
         * Move the label ABOVE the reference line.
         * -100% moves the complete label height upward.
         */
        top: thresholdY,
        transform: 'translateY(-100%)',
        marginTop: '-3px',
      }}
    >
      {threshold}m threshold
    </div>
  );
};

/* ============================================================================
 * COMPONENT
 * ========================================================================== */

const HourlyActiveMinutesChart = ({
  data,
  target,
}: Props) => {
  /* ==========================================================================
     API RESPONSE
  ========================================================================== */

  const apiResponse =
    !Array.isArray(data) &&
    data
      ? data
      : null;

  /* ==========================================================================
     API LIST
  ========================================================================== */

  const apiList =
    Array.isArray(data)
      ? data
      : apiResponse
          ?.employeeHourlyActiveMinutesList ??
        EMPTY_HOURLY_ACTIVE_MINUTES;

  /* ==========================================================================
     TARGET
  ========================================================================== */

  const rawTarget =
    target ??
    apiResponse
      ?.hourlyActiveMinutesTarget ??
    45;

  const numericTarget =
    Number(rawTarget);

  const threshold =
    Number.isFinite(
      numericTarget,
    ) &&
    numericTarget > 0
      ? numericTarget
      : 45;

  /* ==========================================================================
     NORMALIZE DATA
  ========================================================================== */

  const chartData =
    useMemo<ChartDataItem[]>(
      () => {
        if (
          !Array.isArray(
            apiList,
          )
        ) {
          return [];
        }

        return apiList
          .map(
            (
              item,
            ) => {
              const hour =
                Number(
                  item?.hour,
                );

              const activeMinutes =
                Number(
                  item?.activeMinutes,
                );

              return {
                time:
                  String(
                    Number.isFinite(
                      hour,
                    )
                      ? hour
                      : 0,
                  ).padStart(
                    2,
                    '0',
                  ),

                hour:
                  Number.isFinite(
                    hour,
                  )
                    ? hour
                    : 0,

                activeMinutes:
                  Number.isFinite(
                    activeMinutes,
                  ) &&
                  activeMinutes > 0
                    ? activeMinutes
                    : 0,
              };
            },
          )
          .sort(
            (
              a,
              b,
            ) =>
              a.hour -
              b.hour,
          );
      },
      [apiList],
    );

  /* ==========================================================================
     FIRST ACTIVE HOUR
  ========================================================================== */

  const firstActiveIndex =
    chartData.findIndex(
      (
        item,
      ) =>
        item.activeMinutes >
        0,
    );

  /* ==========================================================================
     LAST ACTIVE HOUR
  ========================================================================== */

  let lastActiveIndex =
    chartData.length - 1;

  while (
    lastActiveIndex >= 0 &&
    chartData[
      lastActiveIndex
    ]?.activeMinutes <= 0
  ) {
    lastActiveIndex -= 1;
  }

  /* ==========================================================================
     VISIBLE DATA
     
     Result:
     
     05
     06
     07
     08
     09
     10
  ========================================================================== */

  const visibleChartData =
    firstActiveIndex >= 0 &&
    lastActiveIndex >=
      firstActiveIndex
      ? chartData.slice(
          firstActiveIndex,
          lastActiveIndex + 1,
        )
      : [];

  /* ==========================================================================
     MAX VALUE
  ========================================================================== */

  const maxDataValue =
    Math.max(
      ...visibleChartData.map(
        (
          item,
        ) =>
          item.activeMinutes,
      ),
      0,
    );

  /* ==========================================================================
     Y AXIS STEP
  ========================================================================== */

  const getYAxisStep = (
    maxValue: number,
  ): number => {
    if (
      maxValue <= 60
    ) {
      return 10;
    }

    if (
      maxValue <= 120
    ) {
      return 20;
    }

    if (
      maxValue <= 300
    ) {
      return 50;
    }

    if (
      maxValue <= 600
    ) {
      return 100;
    }

    return 200;
  };

  const yAxisStep =
    getYAxisStep(
      Math.max(
        maxDataValue,
        threshold,
      ),
    );

  /* ==========================================================================
     Y AXIS MAX
  ========================================================================== */

  const yAxisMax =
    Math.max(
      yAxisStep,
      Math.ceil(
        Math.max(
          maxDataValue,
          threshold,
        ) /
          yAxisStep,
      ) *
        yAxisStep,
    );

  /* ==========================================================================
     Y AXIS TICKS
  ========================================================================== */

  const yAxisTicks =
    Array.from(
      {
        length:
          Math.floor(
            yAxisMax /
              yAxisStep,
          ) + 1,
      },
      (
        _,
        index,
      ) =>
        index *
        yAxisStep,
    );

  /* ==========================================================================
     DATA LENGTH
  ========================================================================== */

  const dataLength =
    visibleChartData.length;

  /* ==========================================================================
     X AXIS INTERVAL
  ========================================================================== */

  const xAxisInterval =
    dataLength <= 12
      ? 0
      : dataLength <= 18
        ? 1
        : 'preserveStartEnd';

  /* ==========================================================================
     BAR SIZE
  ========================================================================== */

  const barSize =
    dataLength <= 8
      ? 30
      : dataLength <= 12
        ? 26
        : 22;

  /* ==========================================================================
     EMPTY STATE
  ========================================================================== */

  if (
    visibleChartData.length ===
    0
  ) {
    return (
      <div
        className="
          w-full
          min-w-0
          rounded-[16px]
          border
          border-[#D0D5DD]
          bg-white
          p-4
        "
      >
        <div
          className="
            flex
            items-center
            gap-2
          "
        >
          <h3
            className="
              text-[15px]
              font-semibold
              text-[#002B5B]
            "
          >
            Hourly Active Minutes
          </h3>

          <span
            className="
              rounded-full
              bg-[#EEF4FF]
              px-3
              py-[4px]
              text-[10px]
              font-medium
              text-[#2563EB]
            "
          >
            Today · {threshold}m
            threshold
          </span>
        </div>

        <div
          className="
            flex
            h-[230px]
            items-center
            justify-center
          "
        >
          <span
            className="
              text-[13px]
              text-[#667085]
            "
          >
            No active minutes
            data available
          </span>
        </div>
      </div>
    );
  }

  /* ==========================================================================
     RENDER
  ========================================================================== */

  return (
    <div
      className="
        w-full
        min-w-0
        rounded-[16px]
        border
        border-[#D0D5DD]
        bg-white
        p-4
      "
    >
      {/* ====================================================================
          HEADER
      ==================================================================== */}

      <div
        className="
          mb-4
          flex
          flex-wrap
          items-center
          gap-2
        "
      >
        <h3
          className="
            text-[15px]
            font-semibold
            text-[#002B5B]
          "
        >
          Hourly Active Minutes
        </h3>

        <span
          className="
            rounded-full
            bg-[#EEF4FF]
            px-3
            py-[4px]
            text-[10px]
            font-medium
            text-[#2563EB]
          "
        >
          Today · {threshold}m
          threshold
        </span>
      </div>

      {/* ====================================================================
          CHART CONTAINER
      ==================================================================== */}

      <div
        className="
          relative
          w-full
          min-w-0
          overflow-visible
        "
        style={{
          height:
            CHART_HEIGHT,
        }}
      >
        {/* ==================================================================
            THRESHOLD LABEL

            RIGHT SIDE
        ================================================================== */}

        <ThresholdLabel
          threshold={
            threshold
          }
          yAxisMax={
            yAxisMax
          }
        />

        {/* ==================================================================
            CHART
        ================================================================== */}

        <ResponsiveContainer
          width="100%"
          height={
            CHART_HEIGHT
          }
        >
          <BarChart
            data={
              visibleChartData
            }
            margin={{
              top: CHART_TOP_MARGIN,
              right: 12,
              left: 0,
              bottom:
                CHART_BOTTOM_MARGIN,
            }}
            barCategoryGap={
              dataLength > 18
                ? '18%'
                : dataLength > 12
                  ? '22%'
                  : '25%'
            }
          >
            {/* ==============================================================
                GRID
            ============================================================== */}

            <CartesianGrid
              vertical={false}
              stroke={
                GRID_COLOR
              }
              strokeDasharray="2 2"
            />

            {/* ==============================================================
                X AXIS
            ============================================================== */}

            <XAxis
              dataKey="time"
              interval={
                xAxisInterval
              }
              minTickGap={12}
              axisLine={false}
              tickLine={false}
              tickMargin={10}
              height={30}
              tick={{
                fontSize: 11,
                fill:
                  AXIS_COLOR,
              }}
              tickFormatter={(
                value,
              ) =>
                `${value}:00`
              }
            />

            {/* ==============================================================
                Y AXIS
            ============================================================== */}

            <YAxis
              domain={[
                0,
                yAxisMax,
              ]}
              ticks={
                yAxisTicks
              }
              allowDecimals={false}
              width={45}
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              tickFormatter={(
                value,
              ) =>
                `${value}m`
              }
              tick={{
                fontSize: 11,
                fill:
                  AXIS_COLOR,
              }}
            />

            {/* ==============================================================
                THRESHOLD LINE
            ============================================================== */}

            <ReferenceLine
              y={
                threshold
              }
              stroke={
                THRESHOLD_COLOR
              }
              strokeDasharray="5 5"
              strokeWidth={1}
              ifOverflow="extendDomain"
            />

            {/* ==============================================================
                SMALL TOOLTIP

                Recharts handles the hover automatically.

                IMPORTANT:
                No manual mouse coordinates.
            ============================================================== */}

            <Tooltip
              cursor={false}
              content={
                <CustomTooltip />
              }
              offset={8}
              allowEscapeViewBox={{
                x: false,
                y: false,
              }}
              wrapperStyle={{
                outline:
                  'none',
                zIndex: 9999,
              }}
            />

            {/* ==============================================================
                ACTIVE BARS
            ============================================================== */}

            <Bar
              dataKey="activeMinutes"
              name="Active mins"
              radius={[
                4,
                4,
                0,
                0,
              ]}
              barSize={
                barSize
              }
              maxBarSize={32}
              isAnimationActive={
                false
              }
            >
              {visibleChartData.map(
                (
                  item,
                  index,
                ) => (
                  <Cell
                    key={`${item.hour}-${index}`}
                    fill={
                      BAR_COLOR
                    }
                  />
                ),
              )}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default HourlyActiveMinutesChart;